import React, { useState, useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import Lozenge from '@atlaskit/lozenge';
import Link from '@atlaskit/link';
import { Anchor, Box, Flex, Grid, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data';
import { usePageContentId } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsDarkMode } from '@confluence/app-navigation/entry-points/useIsDarkMode';
import { FeedbackSource, LazyGlobalFeedbackCollector } from '@confluence/global-feedback-collector';

import { EditorControlsFeedbackCollector } from './EditorControlsFeedbackCollectorLoader';
import type { BannerType } from './EditorControlsBannerDismissal';
import toolbarButtonImage from './assets/toolbar-button.svg';
import { ChangeBoardingLight, ChangeBoardingDark } from './ChangeBoardingSvg';

const layoutArea = 'editor-controls-changeboarding-layout-area';

const feedbackBannerStyles = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
	color: 'color.text.subtle',
	padding: 'space.150',
	backgroundColor: 'color.background.accent.purple.subtlest',
	alignItems: 'center',
	justifyItems: 'end',
});

const changeBoardingImageAddStyles = css({
	justifySelf: 'start',
	width: '477px',
	height: '81px',
	gridArea: 'image',
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries, no-useless-computed-key, @atlaskit/ui-styling-standard/no-unsafe-values
	[`@container ${layoutArea} (min-width: 64rem)`]: {
		display: 'grid',
	},
});

const breakpointContainerQuery = `@container ${layoutArea} (min-width: 67.5rem)`;

const shortChangeBoardingImageAddStyles = css({
	justifySelf: 'start',
	gridArea: 'image',
	display: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[breakpointContainerQuery]: {
		display: 'grid',
	},
});

const feedbackBannerNewPinningStyles = xcss({
	containerType: 'inline-size',
	containerName: layoutArea,
	padding: 'space.0',
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
	textAlign: 'center',
	display: 'inline-block',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 'space.100',
	wordBreak: 'break-word',
});

const feedbackBannerContainerStyles = xcss({
	gridArea: 'message',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	wordBreak: 'break-word',
	flexWrap: 'wrap',
});

const feedbackBannerMessagePinningStyles = xcss({
	gridArea: 'message',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	wordBreak: 'break-word',
	flexWrap: 'wrap',
	padding: 'space.150',
});

const shortFeedbackBannerMessagePinningStyles = css({
	gridArea: 'message',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	wordBreak: 'break-word',
	flexWrap: 'wrap',
	padding: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values
	[breakpointContainerQuery]: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},
});

const closeButtonNewStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
	paddingRight: 'space.150',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

const i18n = defineMessages({
	bannerHighlightedMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.highlighted-message',
		defaultMessage: '<strong>Looking for your toolbar?</strong>',
		description: 'This is the highlighted message on the editor controls onboarding banner.',
	},
	bannerClose: {
		id: 'editor-action-bar.editor-controls-onboarding.close',
		defaultMessage: 'Close',
		description: 'clicking this icon closes the onboarding banner',
	},
	lozengeMessage: {
		id: 'editor-action-bar.editor-controls-onboarding.lozenge',
		defaultMessage: 'New',
		description: 'This is the message on the lozenge in the editor controls onboarding banner',
	},
	collectorTitle: {
		id: 'editor-action-bar.feedback-collector.title',
		defaultMessage: 'Give feedback',
		description: 'title for the feedback collector',
	},
	collectorTitleDetails: {
		id: 'editor-action-bar.feedback-collector.title-details',
		defaultMessage:
			'Your thoughts are valuable and help us improve. {lineBreak}{lineBreak} If you’d like to chat with us further and stay informed about this Early Access Program, join our {communityGroupLink}.',
		description:
			"This message is seeking the user's input for improvements and invites them to join a specific group for more information and updates about the Early Access Program.",
	},
	collectorCommunityGroup: {
		id: 'editor-action-bar.feedback-collector.community-group',
		defaultMessage: 'Community group',
		description:
			'Clicking this message takes the user to the community group for the Early Access Program.',
	},
	imageAltText: {
		id: 'editor-action-bar.editor-controls-onboarding.image-alt-text',
		defaultMessage: 'New formatting toolbar button',
		description: 'This is the alt text for the image in the editor controls onboarding banner',
	},
	shareYourFeedback: {
		id: 'editor-action-bar.editor-controls-onboarding.share-your-feedback',
		defaultMessage: 'Share your <feedback>feedback</feedback>.',
		description: 'This is the message on the editor controls onboarding banner',
	},
	bannerMessageForTopToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-top-toolbar',
		defaultMessage:
			'Keep your toolbar at the top or try the <link>in-line toolbar</link>. It appears when you select text.',
		description:
			'This is the message on the editor controls onboarding banner for the classic pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForInlineToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-inline-toolbar',
		defaultMessage: 'Select text to open your <link>in-line toolbar</link>.',
		description:
			'This is the message on the editor controls onboarding banner for the classic pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForLivePagesInlineToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-live-pages-inline-toolbar',
		defaultMessage: 'Select text for your <link>in-line toolbar to appear</link>.',
		description:
			'This is the message on the editor controls onboarding banner for the live pages. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForNewPinningInlineToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-new-pinning-inline-toolbar',
		defaultMessage: 'Try the <link>new unpinned toolbar</link> that appears when you select text.',
		description:
			'This is the message on the editor controls onboarding banner for the new toolbar pinning inline toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForNewPinningTopToolbar: {
		id: 'editor-action-bar.editor-controls-onboarding.message-new-pinning-top-toolbar',
		defaultMessage:
			'Keep your toolbar at the top, or unpin it to try the <link>in-line toolbar</link>. It appears when you select text.',
		description:
			'This is the message on the editor controls onboarding banner for the new toolbar pinning top toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForPinningTopToolbarShort: {
		id: 'editor-action-bar.editor-controls-onboarding.message-pinning-inline-toolbar-short',
		defaultMessage: 'Try the <link>inline toolbar</link>. Unpin it. Select text to edit.',
		description:
			'Shorter message on the editor controls onboarding banner for the new toolbar pinning inline toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	bannerMessageForPinningInlineToolbarShort: {
		id: 'editor-action-bar.editor-controls-onboarding.message-pinning-top-toolbar-short',
		defaultMessage: '<link>Inline toolbar</link>. Select text to edit. Or pin back to the top.',
		description:
			'Shorter message on the editor controls onboarding banner for the new toolbar pinning top toolbar. It informs the user that the toolbar will be shown when content is selected',
	},
	shareYourFeedbackShort: {
		id: 'editor-action-bar.editor-controls-onboarding.share-your-feedback-short',
		defaultMessage: '<feedback>Feedback</feedback>',
		description: 'Shorter message on the editor controls onboarding banner for sharing feedback',
	},
});

export const LEARN_MORE_LINK_URL =
	'https://support.atlassian.com/confluence-cloud/docs/how-to-use-the-new-toolbar-and-insert-elements/';

export const COMMUNITY_LINK_URL =
	'https://community.atlassian.com/forums/Confluence-articles/Live-Docs-Beta-update-Control-where-the-edit-tool-bar-lives/ba-p/3005144#M13863';

const CommunityGroupLink = () => (
	<Anchor href={COMMUNITY_LINK_URL} target="_blank">
		<FormattedMessage {...i18n.collectorCommunityGroup} />
	</Anchor>
);

const ATLASSIAN_ORG_ID = '2346a038-3c8c-498b-a79b-e7847859868d'; // using the atlassian org id to distinguish between internal and external orgs

export type EditorControlsOnboardingBannerProps = {
	onDismiss: () => void;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	bannerType: BannerType;
};

const getRevisedBannerMessage = (isLivePage: boolean, bannerType: BannerType) => {
	if (isLivePage && bannerType === 'toolbar-inline') {
		return i18n.bannerMessageForLivePagesInlineToolbar;
	}
	return bannerType === 'toolbar-top'
		? i18n.bannerMessageForTopToolbar
		: i18n.bannerMessageForInlineToolbar;
};

const getNewPinningBannerMessage = (bannerType: BannerType) => {
	if (bannerType === 'toolbar-inline') {
		return i18n.bannerMessageForNewPinningInlineToolbar;
	}
	return i18n.bannerMessageForNewPinningTopToolbar;
};

const getShortPinningBannerMessage = (bannerType: BannerType) => {
	if (bannerType === 'toolbar-inline') {
		return i18n.bannerMessageForPinningInlineToolbarShort;
	}
	return i18n.bannerMessageForPinningTopToolbarShort;
};

const BannerStyleWrapper = ({ children }: { children: React.ReactNode }) => {
	if (fg('platform_editor_controls_cb_banner_size')) {
		return <div css={shortFeedbackBannerMessagePinningStyles}>{children}</div>;
	}

	return <Flex xcss={feedbackBannerMessagePinningStyles}>{children}</Flex>;
};

const BannerImageStyleWrapper = ({ children }: { children: React.ReactNode }) => {
	if (fg('platform_editor_controls_cb_banner_size')) {
		return <div css={shortChangeBoardingImageAddStyles}>{children}</div>;
	}

	return <div css={changeBoardingImageAddStyles}>{children}</div>;
};

export const EditorControlsBanner = ({
	onDismiss,
	triggerAnalyticsEvent,
	isLivePage,
	isEmbeddedConfluence,
	bannerType,
}: EditorControlsOnboardingBannerProps) => {
	const isDarkMode = useIsDarkMode();
	const { formatMessage } = useIntl();
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	const [contentId] = usePageContentId();
	const { orgId } = useSessionData();
	const isInternalUser = orgId === ATLASSIAN_ORG_ID;

	useEffect(() => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.displayed', {
				isLivePage,
			});
		}
	}, [triggerAnalyticsEvent, isLivePage]);

	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	const handleOnClickClose = () => {
		if (triggerAnalyticsEvent) {
			void triggerAnalyticsEvent('controls.banner.dismissed', {
				isLivePage,
			});
		}
		onDismiss();
	};

	const handleOnClickFeedbackButton = () => {
		setShowFeedbackCollector(true);
	};

	const handleSubmitFeedback = () => {
		handleOnClickClose();
		setShowFeedbackCollector(false);
	};

	if (isEmbeddedConfluence) {
		return null;
	}

	const ChangeBoardingImage = isDarkMode ? ChangeBoardingDark : ChangeBoardingLight;

	if (expValEquals('platform_editor_controls_toolbar_pinning_exp', 'isEnabled', true)) {
		return (
			<div data-vc="editor-controls-onboarding-banner" {...ssrPlaceholderIdProp}>
				<Grid
					templateAreas={['image message close']}
					templateColumns={`${fg('platform_editor_controls_cb_banner_size') ? 'minmax(max-content, 1fr)' : '1fr'} auto 1fr`}
					xcss={[feedbackBannerStyles, feedbackBannerLayerStyles, feedbackBannerNewPinningStyles]}
					testId="editor-controls-onboarding-banner"
				>
					<BannerImageStyleWrapper>
						<ChangeBoardingImage
							title={formatMessage(i18n.imageAltText)}
							titleId={`onboarding-banner-image-${isDarkMode ? 'dark' : 'light'}`}
						/>
					</BannerImageStyleWrapper>
					<BannerStyleWrapper>
						<Flex xcss={feedbackBannerMessageStyles}>
							<Lozenge appearance="new" isBold>
								{formatMessage(i18n.lozengeMessage)}
							</Lozenge>{' '}
							{fg('platform_editor_controls_cb_banner_size')
								? null
								: isLivePage &&
									bannerType === 'toolbar-inline' && (
										<FormattedMessage
											{...i18n.bannerHighlightedMessage}
											values={{
												strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
											}}
										/>
									)}{' '}
							<FormattedMessage
								{...(fg('platform_editor_controls_cb_banner_size')
									? getShortPinningBannerMessage(bannerType)
									: getNewPinningBannerMessage(bannerType))}
								values={{
									link: (chunks: React.ReactNode[]) => (
										<Anchor href={LEARN_MORE_LINK_URL} target="_blank">
											{chunks}
										</Anchor>
									),
									strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
								}}
							/>{' '}
							<FormattedMessage
								{...(fg('platform_editor_controls_cb_banner_size')
									? i18n.shareYourFeedbackShort
									: i18n.shareYourFeedback)}
								values={{
									feedback: (chunks: React.ReactNode[]) => (
										// eslint-disable-next-line jsx-a11y/anchor-is-valid
										<Link href="#" onClick={handleOnClickFeedbackButton}>
											{chunks}
										</Link>
									),
								}}
							/>
						</Flex>
					</BannerStyleWrapper>
					<Box xcss={closeButtonNewStyles}>
						<IconButton
							appearance="subtle"
							spacing="compact"
							icon={CrossIcon}
							label={formatMessage(i18n.bannerClose)}
							onClick={handleOnClickClose}
						/>
					</Box>
				</Grid>
				{showFeedbackCollector &&
					(fg('platform_editor_controls_feedback_consolidation') ? (
						<LazyGlobalFeedbackCollector
							onClose={() => setShowFeedbackCollector(false)}
							onCloseComplete={() => {}}
							onSubmit={handleSubmitFeedback}
							feedbackSource={FeedbackSource.EDITOR_CONTROLS}
							additionalFields={{
								source: 'editor-controls-feedback-banner',
							}}
						/>
					) : (
						<EditorControlsFeedbackCollector
							feedbackTitle={formatMessage(i18n.collectorTitle)}
							feedbackTitleDetails={
								formatMessage(i18n.collectorTitleDetails, {
									lineBreak: <br />,
									communityGroupLink: <CommunityGroupLink />,
								}) as React.ReactChild
							}
							onClose={() => setShowFeedbackCollector(false)}
							onSubmit={handleSubmitFeedback}
							source="editor-controls-feedback-banner"
							contentId={contentId}
							isLivePage={isLivePage}
							isInternalUser={isInternalUser}
						/>
					))}
			</div>
		);
	}
	return (
		<div data-vc="editor-controls-onboarding-banner" {...ssrPlaceholderIdProp}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={[feedbackBannerStyles, feedbackBannerLayerStyles]}
				testId="editor-controls-onboarding-banner"
			>
				<Flex xcss={feedbackBannerContainerStyles}>
					<Image
						src={toolbarButtonImage}
						alt={formatMessage(i18n.imageAltText)}
						width={197}
						height={32}
					/>
					<Flex xcss={feedbackBannerMessageStyles}>
						<Lozenge appearance="new" isBold>
							{formatMessage(i18n.lozengeMessage)}
						</Lozenge>{' '}
						{isLivePage && bannerType === 'toolbar-inline' && (
							<FormattedMessage
								{...i18n.bannerHighlightedMessage}
								values={{
									strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
								}}
							/>
						)}{' '}
						<FormattedMessage
							{...getRevisedBannerMessage(isLivePage, bannerType)}
							values={{
								link: (chunks: React.ReactNode[]) => (
									<Anchor href={LEARN_MORE_LINK_URL} target="_blank">
										{chunks}
									</Anchor>
								),
								strong: (chunks: React.ReactNode[]) => <Text weight="bold">{chunks}</Text>,
							}}
						/>{' '}
						<FormattedMessage
							{...i18n.shareYourFeedback}
							values={{
								feedback: (chunks: React.ReactNode[]) => (
									// eslint-disable-next-line jsx-a11y/anchor-is-valid
									<Link href="#" onClick={handleOnClickFeedbackButton}>
										{chunks}
									</Link>
								),
							}}
						/>
					</Flex>
				</Flex>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>
			{showFeedbackCollector && (
				<EditorControlsFeedbackCollector
					feedbackTitle={formatMessage(i18n.collectorTitle)}
					feedbackTitleDetails={
						formatMessage(i18n.collectorTitleDetails, {
							lineBreak: <br />,
							communityGroupLink: <CommunityGroupLink />,
						}) as React.ReactChild
					}
					onClose={() => setShowFeedbackCollector(false)}
					onSubmit={handleSubmitFeedback}
					source="editor-controls-feedback-banner"
					contentId={contentId}
					isLivePage={isLivePage}
					isInternalUser={isInternalUser}
				/>
			)}
		</div>
	);
};
